import React, { useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, FormControlLabel, Modal, Switch, TextField } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DealViews } from "constants/deal";
import { Routes } from "constants/routes";
import { useActionHook, useDidUpdate, useModals, useUrlHooks } from "hooks";
import { useDealQueriesSelect, useReportsQueriesSelector, useUserActions } from "store";
import { IReportType } from "store/types";
import { ISaveViewModal } from "store/types/modals";
import { Userpilot } from "userpilot";

import "./SaveViewModal.styles.scss";

export const SaveViewModal: React.FC<{ data: ISaveViewModal }> = ({ data }) => {
  const { isOpen, target, type, currentLocation } = data;
  const { closeSaveViewModal } = useModals();
  const [inputValue, setInputValue] = useState<string>("");
  const [defaultView, setIsDefaultView] = useState<boolean | undefined>(undefined);

  const { view } = useDealQueriesSelect();

  const { createBookmarkAction } = useUserActions();
  const queries = useReportsQueriesSelector();

  const { pathname, search } = useLocation();
  const { queryParams } = useUrlHooks();

  const currentReportPage = useMemo(() => {
    if (currentLocation === Routes.snapshot) return IReportType.PORTFOLIO;
    if (currentLocation === Routes.timeline) return IReportType.PORTFOLIO;
    if (currentLocation === Routes.comparison) return IReportType.COMPARISON;
    if (currentLocation === Routes.classic) return IReportType.PORTFOLIO;
    if (currentLocation === Routes.cashflow) return IReportType.PORTFOLIO;
    if (currentLocation && matchPath(currentLocation, Routes.dealReport)) return IReportType.DEAL_VIEW;
  }, [currentLocation]);

  useDidUpdate(() => {
    if (!isOpen && defaultView) {
      setIsDefaultView(false);
    }
  }, [isOpen]);

  const onSubmit = () => {
    if (!target || !type) return;
    if (inputValue) {
      let resultQuery: string = "";

      if (type === "report") {
        resultQuery = JSON.stringify(queries);
      } else if (type === "data_operations") {
        return createBookmarkAction({
          name: inputValue,
          params: pathname + (search || ""),
          type,
          is_default: defaultView
        });
      } else {
        const valueToSave = queryParams[target];
        if (valueToSave) {
          const queryObject = JSON.parse(valueToSave as string);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { deal, ...rest } = queryObject; //WE DON'T NEED TO SAVE DEAL IN URL OF BOOKMARK
          resultQuery = JSON.stringify(rest);
        }
      }

      if (matchPath(pathname, Routes.myDeals) && view === DealViews.Dashboard && defaultView) {
        return createBookmarkAction({
          name: inputValue,
          params: pathname + `?${target}=` + encodeURIComponent(resultQuery),
          type: "investor_view",
          report_type: currentReportPage || undefined
        });
      }

      if (resultQuery) {
        return createBookmarkAction({
          name: inputValue,
          params: pathname + `?${target}=` + encodeURIComponent(resultQuery),
          type,
          report_type: currentReportPage || undefined,
          is_default: defaultView
        });
      }
    }
  };

  const { action } = useActionHook(onSubmit, {
    onSuccess: () => {
      closeSaveViewModal();
      if (type === "report") {
        Userpilot.track("Save report view");
      }
    }
  });

  return (
    <Modal
      open={isOpen}
      onClose={closeSaveViewModal}
      slotProps={{
        backdrop: {
          style: { backgroundColor: "transparent" }
        }
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: "8%",
          right: "14%",
          bgcolor: "background.paper",
          boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.5)",
          borderRadius: "8px",
          width: 420,
          outline: "none"
        }}
      >
        <div className="save-view-modal__content">
          <div className="save-view-modal__head">
            <span>Save view</span>
            <MaterialTooltip
              isShow={true}
              placement="bottom-end"
              title="Saving this view will retain all the changes you have made in the search and the filters."
            >
              <InfoOutlinedIcon sx={{ fontSize: "25px" }} />
            </MaterialTooltip>
          </div>
          <div className="save-view-modal__body">
            <div className="save-view-modal__input-wrap">
              <TextField
                sx={{ marginBottom: "14px", width: "100%", flexGrow: 1, marginTop: "18px" }}
                onChange={(e) => {
                  setInputValue(e.target.value);
                }}
                label="Name"
                placeholder="View name"
              />
              {matchPath(pathname, Routes.myDeals) && view === DealViews.Dashboard && (
                <FormControlLabel
                  control={
                    <Switch
                      sx={{
                        color: "#173c4c",
                        "& .MuiSwitch-switchBase": {
                          "&:hover": {
                            background: "none"
                          },
                          padding: "12px"
                        },
                        "& .MuiSwitch-switchBase.MuiSwitch-colorPrimary": {
                          color: "#bac7ce",
                          boxShadow: "none"
                        },
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#26a6ae",
                          "&:hover": {
                            background: "none"
                          }
                        },
                        "& .MuiSwitch-track": {
                          color: "#bac7ce",
                          height: "20px",
                          borderRadius: "9px"
                        }
                      }}
                      checked={defaultView}
                      onChange={() => setIsDefaultView((prev) => !prev)}
                    />
                  }
                  labelPlacement="end"
                  label="Set as Investor View Default"
                />
              )}
              {!matchPath(pathname, Routes.myDeals) && (
                <FormControlLabel
                  control={
                    <Switch
                      sx={{
                        color: "#173c4c",
                        "& .MuiSwitch-switchBase": {
                          "&:hover": {
                            background: "none"
                          },
                          padding: "12px"
                        },
                        "& .MuiSwitch-switchBase.MuiSwitch-colorPrimary": {
                          color: "#bac7ce",
                          boxShadow: "none"
                        },
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#26a6ae",
                          "&:hover": {
                            background: "none"
                          }
                        },
                        "& .MuiSwitch-track": {
                          color: "#bac7ce",
                          height: "20px",
                          borderRadius: "9px"
                        }
                      }}
                      checked={defaultView}
                      onChange={() => setIsDefaultView((prev) => !prev)}
                    />
                  }
                  labelPlacement="end"
                  label="Set as Default View"
                />
              )}
            </div>
          </div>
          <div className="save-view-modal__footer">
            <Button variant="outlined" onClick={closeSaveViewModal}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!inputValue) return;
                action();
              }}
              variant="contained"
            >
              Save
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
